import { useEffect } from "react";
import { useTenant } from "@/lib/tenant-context.js";
import { basePath } from "@/lib/session.js";
import { useAsync } from "@/hooks/use-async.js";
import { Button } from "@/components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Badge } from "@/components/ui/badge.js";
import { useToast } from "@/components/ui/toast.js";
import { DataTable } from "@/views/data-table.js";

/**
 * Calendar sync admin per Flow F. Per-resource Connect Google / Connect Outlook
 * buttons; lists existing links with freshness state and pending sync count.
 */
export function CalendarSyncPage(): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const links = useAsync(() => api.listCalendarLinks(), [api]);
  const resources = useAsync(() => api.listResources(), [api]);

  // The OAuth callback bounces back here with ?connected=<provider>.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    if (connected) {
      toast({ title: `Connected ${connected === "google" ? "Google Calendar" : "Outlook calendar"}`, variant: "success" });
      params.delete("connected");
      const next = params.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${next ? `?${next}` : ""}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (links.loading || resources.loading) return <Skeleton className="h-72 w-full" />;

  const resourceMap = new Map((resources.data?.data ?? []).map((r) => [r.id, r]));

  function start(provider: "google" | "outlook", resourceId: string): void {
    // OAuth start returns a 302 to the provider; navigate the browser there
    // directly so the redirect lands at Google / Microsoft, not back inside
    // a fetch promise that swallows the response. The provider redirects to
    // the API callback (the session cookie rides the top-level GET), which
    // exchanges the code and 302s back to this page with ?connected=provider.
    // basePath() keeps the embedded mount working: ronic.ai/calendar/* routes
    // the API at /calendar/api/*, not /api/* (same rule as ApiClient.request).
    const redirectBack = `${window.location.origin}${basePath()}/api/v1/calendar-sync/oauth/${provider}/callback`;
    window.location.href = api.oauthStartUrl(provider, resourceId, redirectBack);
  }

  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-lg font-semibold">Calendar sync</h1>
      <Card>
        <CardHeader>
          <CardTitle>Connect a resource</CardTitle>
          <CardDescription>Each resource can be linked to one Google and one Outlook calendar.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {(resources.data?.data ?? [])
            .filter((r) => !r.deleted_at)
            .map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2">
                <span className="text-sm font-medium">{r.name}</span>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => start("google", r.id)}>
                    Connect Google
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => start("outlook", r.id)}>
                    Connect Outlook
                  </Button>
                </div>
              </div>
            ))}
        </CardContent>
      </Card>

      <DataTable
        rows={links.data?.data ?? []}
        columns={[
          { header: "Provider", cell: (l) => <span className="capitalize">{l.provider}</span> },
          { header: "Resource", cell: (l) => resourceMap.get(l.resource_id)?.name ?? l.resource_id.slice(0, 8) },
          {
            header: "Freshness",
            cell: (l) =>
              l.reauth_required ? (
                <Badge variant="destructive">reconnect required</Badge>
              ) : (
                <Badge variant={l.freshness_state === "fresh" ? "success" : "warning"}>
                  {l.freshness_state}
                </Badge>
              )
          },
          { header: "Pending", cell: (l) => l.pending_sync_count ?? 0 },
          {
            header: "",
            cell: (l) => (
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    await api.deleteCalendarLink(l.id);
                    toast({ title: "Disconnected", variant: "success" });
                    links.reload();
                  } catch (error) {
                    toast({ title: "Could not disconnect", description: String(error), variant: "destructive" });
                  }
                }}
              >
                Disconnect
              </Button>
            )
          }
        ]}
      />
    </div>
  );
}
